import type { useAuth } from "@clerk/react"
import type { QueryClient } from "@tanstack/react-query"
import { redirect } from "@tanstack/react-router"

import type { MePublic } from "@/lib/me-api"
import { ensureMe } from "@/lib/me-loader"

/** Passed into `createRootRouteWithContext` and available as `context` in `beforeLoad`. */
export type RouterContext = {
  auth: ReturnType<typeof useAuth>
  queryClient: QueryClient
}

const signInPath = "/sign-in/$" as const
const signInParams = { _splat: "" } as const

/** Public routes only: if the user is signed in, send them to the app shell. */
export function requireGuest(context: RouterContext): void {
  if (context.auth.isSignedIn) {
    throw redirect({ to: "/dashboard" })
  }
}

/** Protected layout / routes: must have a session token. */
export async function requireAuth(context: RouterContext): Promise<void> {
  const token = await context.auth.getToken()
  if (!token) {
    throw redirect({ to: signInPath, params: signInParams })
  }
}

/** `/` — signed in → dashboard, otherwise → sign-in. */
export function redirectRoot(context: RouterContext): never {
  if (context.auth.isSignedIn) {
    throw redirect({ to: "/dashboard" })
  }
  throw redirect({ to: signInPath, params: signInParams })
}

/**
 * `beforeLoad` helper: prefetch `/api/users/me` (TanStack Query cache) and require a permission.
 * Not a React hook — safe to call from route loaders (unlike `useHasPermission`).
 * Returns `undefined` (without redirecting) when `ensureMe` reports the deactivated-membership
 * state (FLO-166) — `AuthenticatedLayout` renders its dedicated screen over the route either way.
 */
export async function requirePermission(
  context: RouterContext,
  permission: string,
  noAccessRedirect: Parameters<typeof redirect>[0],
): Promise<MePublic | undefined> {
  const me = await ensureMe(context)
  if (!me) return undefined
  if (!me.permissions.includes(permission)) {
    throw redirect(noAccessRedirect)
  }
  return me
}

/**
 * `beforeLoad` helper for platform-level pages (e.g. Feature Flags): staff only.
 * Same cache/prefetch behavior (including the deactivated-membership handling) as `requirePermission`.
 */
export async function requireStaff(
  context: RouterContext,
  noAccessRedirect: Parameters<typeof redirect>[0],
): Promise<MePublic | undefined> {
  const me = await ensureMe(context)
  if (!me) return undefined
  if (!me.is_staff) {
    throw redirect(noAccessRedirect)
  }
  return me
}
